/**
 * `/app/files` — `mockups/client/files.html`.
 *
 * Everything the studio has made, in three views over **two** requests, and the
 * seg costs neither of them.
 *
 * `GET /api/clients/me/files?period=` answers the month, the projects and the
 * library together (`ClientFilesPageSchema`), so switching view writes a search
 * parameter and nothing goes back to the server. The URL is the state and the back
 * button works — `?view=project&period=2026-07` is a link somebody can send, and
 * `routes/app/files.tsx` validates both keys so it stays one.
 *
 * The second request is the credential list, and it is `DelegatedAccess`'s own
 * rather than this file's: the panel is behind one segment, and fetching it here
 * meant the two views that never show it asked for it on every visit. A hook
 * cannot be called conditionally, so the component that needs the data is the
 * component that fetches it — `RequestComposer` does the same with the catalogue.
 *
 * The right-hand column does not move with the seg. A monthly report and a logo
 * pack belong to no month and no project, which is where the mockup puts them and
 * why they are not a fourth tab.
 *
 * **One hero:** the count of files delivered this month, and the month view is the
 * only one that has one. **One policy fold:** `HowThisWorks`, carrying the three
 * sentences the mockup spreads across four panels (DESIGN.md P7). **No ochre**,
 * unless the month holds something out for review: the handover gate is a dashed
 * `.locked` block naming the milestone that opens it, not a border around a card
 * whose other rows are downloadable (P4 — ochre is a decision or a cost).
 *
 * Nothing on this page is computed. Every figure arrives as an integer and is
 * formatted by `useFormat()` — `bytes` and `size` through `format.bytes`, which is
 * where `3,755,840,000` becomes the mockup's own `3.8 GB`, in
 * `packages/shared/src/format.ts` beside every other number rule.
 *
 * **A `billing` seat is refused the page, inline.** `.docs/19` §Edge cases names
 * files as one of the three, and it is the sharpest of them: this page carries the
 * deliverables *and* `DelegatedAccess`, which is the list of the studio's keys to the
 * client's own Google and Odoo accounts. That panel already refuses the **Reveal**
 * control to everybody but an owner or an admin (`mayRevealCredential`), and the
 * masks and labels were still being served to a role `.docs/19` says does not read
 * this page at all. The refusal is above both, and the credential query goes with it.
 */

import { parsePeriod, periodOf, PeriodSchema } from '@fareast/shared'
import { z } from 'zod'

import { useFilesPage } from '../../../api/client/index.js'
import { BundleDownloadButton } from './DownloadLink.js'
import { PeriodSeg } from '../../../components/client/index.js'
import { Seg, SegItem } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Grid, Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { usePrincipal } from '../../../auth/principal.js'
import { useFormat } from '../../../format/useFormat.js'
import { now } from '../../../integrations/clock.js'
import { useSearchState } from '../../../search/useSearchState.js'
import { PageTitle } from '../../../shell/PageTitle.js'
import { RoleRefusal } from '../RoleRefusal.js'
import { abilitiesOf } from '../settings/roles.js'
import { DelegatedAccess } from './DelegatedAccess.js'
import { Library } from './Library.js'
import { MonthFiles } from './MonthFiles.js'
import { ProjectFiles } from './ProjectFiles.js'

/** The three the mockup's topbar offers, in its order. */
export const FILES_VIEWS = ['month', 'project', 'credentials'] as const

export type FilesView = (typeof FILES_VIEWS)[number]

const VIEW_LABELS: Readonly<Record<FilesView, string>> = {
  month: 'By month',
  project: 'By project',
  credentials: 'Credentials',
}

/**
 * What the URL holds — and, from `routes/app/files.tsx`, what the **route**
 * validates, which is what makes `<Link to="/app/files" search={{ view, period }}>`
 * a thing this page can write.
 *
 * Both keys are optional and both `.catch`, so `/app/files`, `?view=nonsense` and
 * `?period=lastyear` all render the same screen rather than an error boundary —
 * `validateSearch` throwing is a route-level failure and a hand-edited URL should
 * show the page. `DEFAULT_VIEW` below is where "absent means by month" lives; absent
 * `period` is decided by nothing here at all — the request goes without one and the
 * answer names the month it chose. Writing either into the schema would make the
 * default a value tests and screenshots have to keep in step with, and for the month
 * it would put a guess about the client's deliveries in the URL validator.
 */
export const FilesSearchSchema = z.object({
  view: z.enum(FILES_VIEWS).optional().catch(undefined),
  period: PeriodSchema.optional().catch(undefined),
})

/** What the seg is on when the URL says nothing. */
const DEFAULT_VIEW: FilesView = 'month'

/** The `<section>`'s accessible name, which is also the visible `<h2>`. */
const HEAD_ID = 'files-head'

export function FilesPage() {
  /*
   * Above the seg, so `?view=credentials` typed by hand is refused with the rest of
   * the page rather than mounting `DelegatedAccess` and asking for the list.
   */
  if (!abilitiesOf(usePrincipal()).raisesRequests) {
    return (
      <>
        <PageTitle eyebrow="Deliverables" title="Files" />
        <RoleRefusal what="files" />
      </>
    )
  }

  return <Files />
}

function Files() {
  const [search, setSearch] = useSearchState(FilesSearchSchema)
  const format = useFormat()
  const view = search.view ?? DEFAULT_VIEW

  /*
   * **With no `?period=` the page does not guess a month.** It asks for none, and
   * renders the one the answer names (`month.period`) — the client's newest month
   * with deliveries that has already begun. Opened from the nav on the 1st, the
   * browser's month is empty: no files, no badge, and a "Download September" button
   * offering a ZIP with nothing but a manifest in it, two clicks from a dashboard
   * saying a file is waiting (session 41, R1-03).
   *
   * The newest month the *control* offers is `latest_period`, and it is a different
   * question. It cannot be `month.period`: looking at July, that is July, and a
   * control counting back from it would drop August off its own right-hand end.
   * `latest_period` is month-independent — a fact about the client rather than about
   * the month on screen — so it does not have that problem, and it stops the seg
   * offering months in which nothing was ever delivered. The browser's clock is the
   * fallback for a client who has no files at all, and for the moment before the
   * answer arrives.
   */
  const files = useFilesPage(search.period)
  const month = files.data?.month
  const period = search.period ?? month?.period
  const latest = files.data?.latest_period ?? periodOf(now())

  const actions = (
    <>
      <Seg label="What to show">
        {FILES_VIEWS.map((one) => (
          <SegItem
            key={one}
            on={view === one}
            onClick={() => {
              setSearch({ view: one })
            }}
          >
            {VIEW_LABELS[one]}
          </SegItem>
        ))}
      </Seg>
      {view === 'month' && period !== undefined ? (
        <>
          <PeriodSeg
            value={period}
            latest={latest}
            onChange={(next) => {
              setSearch({ period: parsePeriod(next) })
            }}
          />
          {/*
           * **A month with nothing the client can open offers no download.** The
           * bundle route answers a ZIP whatever the month holds, and puts only
           * *released* rows in it (`modules/files/routes.ts`, `isReleased`) — so for
           * a month with nothing in it that is a manifest and no files, a button that
           * cannot keep its own promise.
           *
           * `month.files > 0` was the first test and it is the wrong one: that count
           * is `periodTotals`' row count, which deliberately **includes held files**
           * (`readmodels/files-client.ts` says why they stay in the list). A month
           * whose rows are all held therefore passed it and downloaded a manifest.
           * The payload already carries the honest fact per row.
           */}
          {month !== undefined && month.entries.some((entry) => entry.released) ? (
            <BundleDownloadButton period={period} label={`Download ${format.month(period)}`} />
          ) : null}
        </>
      ) : null}
    </>
  )

  return (
    <>
      <PageTitle
        eyebrow="Deliverables"
        title="Files"

        actions={actions}
      />
      <Section aria-labelledby={HEAD_ID}>
        <SectionHead id={HEAD_ID} title="Deliverables" />
        {files.data === undefined ? (
          <Empty>Loading your files…</Empty>
        ) : (
          <Grid cols="3-2" top>
            <Stack gap="loose">
              {view === 'month' ? (
                <MonthFiles month={files.data.month} earlier={files.data.earlier} />
              ) : null}
              {view === 'project' ? <ProjectFiles projects={files.data.projects} /> : null}
              {view === 'credentials' ? <DelegatedAccess /> : null}
            </Stack>
            <Stack gap="loose">
              <Library library={files.data.library} />
            </Stack>
          </Grid>
        )}
      </Section>
    </>
  )
}
